
import { mapMutations, mapGetters, mapState, mapActions } from "vuex";
import { isEqual } from "lodash";
import { diamondKnowledgeOnSlug } from "~/config";
// import UiBreadcrumbs from "@/components/UI/UiBreadcrumbs.vue";
import ProductTabs from "@/components/pages/product/ProductTabs.vue";
import ProductAccordion from "@/components/pages/product/ProductAccordion.vue";
import BlocksContainer from "~/components/blocks/BlocksContainer";
import ProductPurchaseForm from "@/components/pages/product/ProductPurchaseForm.vue";
// import InDivConfigurator from "@/components/pages/product/InDivConfigurator.vue";
import { metaGeneratorPDP, DataURIToBlob, getProductImages } from "@/utils";
import googleAnalyticsEvents from "~/mixins/googleAnalyticsEvents";
import facebookPixelEvents from "~/mixins/facebookPixelEvents";
import useProductMixin from "~/mixins/useProductMixin.js";
import PerfectComplimentBlock from "~/components/pages/product/PerfectComplimentBlock.vue";
import useEvents from "~/mixins/useEvents";
import DiamondKnowledge from "~/components/pages/product/DiamondKnowledge.vue";
import PdpServicesBlock from "~/components/blocks/CustomBlocks/PdpServicesBlock.vue";
import jsonLdMixin from "~/mixins/jsonLdMixin";
import SalePDPBlock from "~/components/blocks/CustomBlocks/SalePDPBlock.vue";
import SizeGuideDrawer from "~/components/common/SizeGuideDrawer.vue";
export default {
  components: {
    SalePDPBlock,
    DiamondKnowledge,
    PdpServicesBlock,
    PerfectComplimentBlock,
    SizeGuideDrawer,
    // PerfectComplimentBlock,
    // UiBreadcrumbs,
    ProductTabs,
    ProductAccordion,
    BlocksContainer,
    ProductPurchaseForm,
    // InDivConfigurator,
  },
  mixins: [
    googleAnalyticsEvents,
    facebookPixelEvents,
    useProductMixin,
    jsonLdMixin,
    useEvents,
  ],
  async asyncData({ $api, error, params, store, route }) {
    const getProduct = async () => {
      try {
        const { data } = await $api.products.getProduct(params.slug);
        return data.data;
      } catch (err) {
        // console.log("err - ", err);
        console.log("err - ", err?.response?.data?.errors);
        // console.log("error status code - ", err.response.status);
        error({ statusCode: err?.response?.status });
      }
    };
    const res = await getProduct();

    const getQuestionForm = async () => {
      try {
        const { data } = await $api.pages.getBlok("product-question-form");
        return data.data;
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
        error({ statusCode: err.response.status });
      }
    };

    const getShipReturnsBlock = async () => {
      try {
        const { data } = await $api.pages.getBlok("product-ship-return");
        return data.data;
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
      }
    };
    const getAskQuestionBlock = async () => {
      try {
        const { data } = await $api.pages.getBlok("product-ask-question");
        return data.data;
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
      }
    };

    const getPdpServicesBlock = async () => {
      try {
        const { data } = await $api.pages.getBlok("pdp-services");
        return data.data;
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
      }
    };

    const getSalePDPBlock = async () => {
      try {
        const { data } = await $api.pages.getBlok("sale-pdp");
        return data.data;
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
      }
    };

    if (res?.embedded) {
      // Prepare additional properties for embedded products
      const additionalProperties = res?.mixedTranslation?.content?.details_map?.length
        ? res.mixedTranslation.content.details_map
            .filter((item) => item.property && item.value)
            .map((item) => ({
              "@type": "PropertyValue",
              name: item.property,
              value: item.value,
            }))
        : null;

      store.commit("general/SET_JSON_SCHEMA_DATA", {
        pageName: res.mixedTranslation?.title,
        pageImage: getProductImages(res)?.[0]?.url || "",
        product: res,
        productSku: route?.query?.config || "",
        additionalProperties,
      });
    }

    return {
      questionFormData: await getQuestionForm(),
      shipReturnData: await getShipReturnsBlock(),
      askQuestionData: await getAskQuestionBlock(),
      pdpServicesData: await getPdpServicesBlock(),
      salePDPData: await getSalePDPBlock(),
      productData: res || null,
      isIndivInited: false,
      count: 0,
      currentConfigurationData: null,
      configurationLoaded: false,
      resizeObserver: null,
      sticky_offset_top: 56, // header size
      sticky_offset_bottom: 84 + 50, // add to bag btn size + padding
      hasEmbeddedPerfectComplimentEl: false,
      hasEmbeddedSalePDPEl: false,
      isMounted: false,
      isSizeGuideDrawerOpen: false,
    };
  },
  head() {
    const i18nHead = this.$nuxtI18nHead({
      addSeoAttributes: true,
    });

    if (i18nHead.link && process.env.DOMAIN) {
      const canonicalLink = i18nHead.link.find(
        (link) => link.rel === "canonical"
      );
      if (canonicalLink) {
        // Check if current configuration is the default configuration
        const currentConfig =
          this.currentConfigurationData?.configuration_id ||
          this.$route.query.configuration_id;
        const isDefaultConfiguration =
          !currentConfig || currentConfig === this.productData?.default_sku;

        if (this.productData?.embedded || isDefaultConfiguration) {
          canonicalLink.href = process.env.DOMAIN + this.$route.path;
        } else {
          canonicalLink.href = process.env.DOMAIN + this.$route.fullPath;
        }
      }
    }

    const script = [];
    if (!this.isIndivLoaded && this.productData && this.productData?.embedded) {
      script.push({
        hid: "extscript",
        // src: `http://localhost:4173/embedded.js?q=${Date.now()}`,
        src: `${process.env.CONFIGURATOR_URL}/embedded.js?q=${Date.now()}`,
        crossorigin: true,
        defer: true,
        callback: () => {
          this.$store.commit("general/SET_INDIV_LOADED", true);
        },
      });
    }

    // JSON-LD for ALL products (embedded and non-embedded)
    if (this.productData && this.jsonSchemaData?.product) {
      // Determine out-of-stock status
      const isOutOfStock = this.productData?.embedded
        ? false // Embedded products handle stock differently
        : !this.jsonSchemaData?.productPrice || this.jsonSchemaData?.productPrice === 0;

      // Get additional properties for schema
      const additionalProperties = this.getAdditionalPropertiesForSchema();

      // Get shipping details (optional - only include if data available)
      // Uses lead_time from configuration/product/settings (priority order: configurationInfo > productData > settings)
      const configurationInfo =
        this.currentConfigurationData || this.jsonSchemaData?.configurationInfo;
      const shippingDetails = this.getShippingDetails({
        configurationInfo,
      });

      const jsonLdPayload = this.setProductPageLdSchema({
        out_of_stock: isOutOfStock,
        additionalProperties,
        shippingDetails,
        // aggregateRating: this.getAggregateRating(), // Optional - implement if data available
      });

      script.push({
        hid: this.getJsonLdScriptHid(jsonLdPayload),
        type: "application/ld+json",
        json: jsonLdPayload,
      });
    }

    const schemaData = this.$store.state.general.jsonSchemaData;
    const titleTemplate = `%s | ${this.getCompanyName}`;
    const description = !this.productData?.embedded ? schemaData.productDescription || "" : this.productData?.mixedTranslation?.description || "";
    return {
      meta: [
        ...metaGeneratorPDP({
          title:
            schemaData.productTitle ||
            schemaData.pageName ||
            schemaData.productTitle,
          description,
          image: schemaData.productImage || "",
          keywords: this.productData?.mixedTranslation?.meta_keywords || "",
          url:
            schemaData.productUrl || process.env.DOMAIN + this.$route.fullPath,
          product_price_amount: schemaData.productPrice,
          product_price_currency: this.settings.currency_code,
          product_retailer_item_id:
            this.$route.query.config || this.productData?.default_sku,
          product_item_group_id: this.productData?.vendor_model,
          company_name: this.getCompanyName,
          titleTemplate,
        }),
        ...i18nHead.meta,
      ],
      htmlAttrs: i18nHead.htmlAttrs,
      link: i18nHead.link,
      title:
        schemaData.productTitle ||
        schemaData.pageName ||
        schemaData.productTitle,
      titleTemplate,
      script,
    };
  },
  computed: {
    ...mapGetters({
      getCompanyName: "general/getCompanyName",
    }),
    ...mapState({
      subscriptionFormData: (state) => state.general.subscriptionFormData,
    }),
    components() {
      return [
        // {
        //   type: { identifier: "RelatedProductBlock" },
        //   content: res?.related,
        // },
        { type: { identifier: "RecentlyViewedBlock" } },
        {
          type: { identifier: "footer-form" },
          mixedTranslation: this.subscriptionFormData.mixedTranslation,
        },
      ];
    },
    isIndivLoaded() {
      return this.$store.state.general.isIndivLoaded;
    },
    cartId() {
      return this.$store.getters.getCartId;
    },
    settings() {
      return this.$store.state.general.settings;
    },
    canSendGoogleAnalyticsStatistic() {
      return (
        this.integrationCallbackStatuses.TYPE_GOOGLE_ANALYTICS &&
        this.configurationLoaded
      );
    },
    canSendFBStatistic() {
      return (
        this.integrationCallbackStatuses.TYPE_FACEBOOK_PIXEL &&
        this.configurationLoaded
      );
    },
    isEmbedded() {
      return this.productData.embedded;
    },
    productTitleBasedOnEmbed() {
      return this.isEmbedded
        ? this.productData?.mixedTranslation?.title
        : this.productTitle;
    },
    productPriceBasedOnEmbed() {
      return this.isEmbedded
        ? this.productData?.base_cost
        : this.configurationPrice;
    },
    hasDiamondKnowledgeCollection() {
      return this.productData?.collections?.some((collection) =>
        [
          diamondKnowledgeOnSlug.zasnuby,
          diamondKnowledgeOnSlug.engagement,
        ]?.includes(collection.slug)
      );
    },
    isRing() {
      return this.productData?.categories?.some(
        (item) => item.slug === "rings"
      );
    },
  },
  watch: {
    isIndivLoaded: {
      async handler(val) {
        if (val && window.confm8ht77 && this.productData.embedded) {
          await window.confm8ht77.init({
            customer_number: this.productData?.customer_number || "",
            // title: this.productData?.mixedTranslation?.title || "",
            default_sku: this.productData?.default_sku || "",
            translations: this.$i18n.messages[this.$i18n.locale].configurator,
            discount:
              this.productData?.sale_value && this.productData?.sale
                ? this.productData.sale_value
                : null,
            currency: this.settings.currency_code,
            lang: this.$i18n.locale,
            size_guide_link: this.sizeGuideUrl || null,
            sticky_offset: 94,
            pdpAddToBag: this.pdpAddToBag,
            onWishlist: this.onWishlist,
            onCompare: this.onCompare,
            onConfigurationChange: this.onConfigurationChange,
            onSizeGuideClick: this.isRing ? this.openSizeGuideDrawer : null,
            ws_url: process.env.WS_CONFIGURATOR,
            ws_auth: process.env.WS_CONFIGURATOR_AUTH_KEY,
            currency_exchange: this.settings?.currency_exchange?.enabled
              ? this.settings?.currency_exchange?.value ?? 1
              : 1,
            priceFormatter: this.$priceFormat,
            sticky_slider_mobile: {
              sticky_offset_top: this.sticky_offset_top,
              sticky_offset_bottom: this.sticky_offset_bottom,
              sticky_container_el: ".product-sticky-container",
              sticky_side: "",
            },
            sticky_add_to_bag_mobile: {
              sticky_container_el: ".product-sticky-container",
              sticky_side: "bottom",
              portal_el: "#add-to-bag-sticky",
            },
            container_class: "container",
            product_details:
              this.productData?.mixedTranslation?.content?.details_map,
            compare_enabled: false,
            enabled_compliment_block: true,
          });
          await this.$nextTick();
          this.isIndivInited = true;
        }
      },
    },
    isIndivInited: {
      handler() {
        const timerId = setInterval(() => {
          const el = document.querySelector(".conf-m8ht77-subtitle-slot");
          if (el) {
            el.innerHTML = `
              <div class="slot-container">
                ${
                  this.productData?.mixedTranslation?.short_description
                    ? `<div class="slot-container__description">${this.productData.mixedTranslation.short_description}</div>`
                    : ""
                }
                <div class="slot-container__tags">${
                  this.productData?.tags?.reduce((acc, el, idx) => {
                    if (idx === 0) {
                      return (acc = acc + el.title);
                    }
                    return (acc = acc + ", " + el.title);
                  }, "") || ""
                }</div>
              </div>
            `;
            clearInterval(timerId);
          }
        }, 20);
        const timerId2 = setInterval(() => {
          const el2 = document.querySelector("#confComplimentPortal");
          if (el2) {
            this.hasEmbeddedPerfectComplimentEl = true;
            clearInterval(timerId2);
          }
        }, 20);
        const timerId3 = setInterval(() => {
          const el3 = document.querySelector("#sale-portal");
          if (el3) {
            this.hasEmbeddedSalePDPEl = true;
            clearInterval(timerId3);
          }
        }, 20);
      },
    },
    canSendGoogleAnalyticsStatistic() {
      if (this.productData.embedded) {
        this.setGaViewItemEvent(this.currentConfigurationData);
      }
    },
    canSendFBStatistic() {
      if (this.productData.embedded) {
        this.fbViewContentEvent(
          this.productData,
          this.currentConfigurationData
        );
      }
    },
  },
  created() {
    this.setBreadcrumbs(this.productTitleBasedOnEmbed);
  },
  async mounted() {
    this.visitEvent();
    this.stickyResizeDiv();
    if (
      !this.isIndivInited &&
      this.isIndivLoaded &&
      this.productData.embedded
    ) {
      await window.confm8ht77.init({
        customer_number: this.productData?.customer_number || "",
        // title: this.productData?.mixedTranslation?.title || "",
        default_sku: this.productData?.default_sku || "",
        translations: this.$i18n.messages[this.$i18n.locale].configurator,
        discount:
          this.productData?.sale_value && this.productData?.sale
            ? this.productData.sale_value
            : null,
        currency: this.settings.currency_code,
        lang: this.$i18n.locale,
        size_guide_link: this.sizeGuideUrl || null,
        sticky_offset: 94,
        pdpAddToBag: this.pdpAddToBag,
        onWishlist: this.onWishlist,
        onCompare: this.onCompare,
        onConfigurationChange: this.onConfigurationChange,
        onSizeGuideClick: this.isRing ? this.openSizeGuideDrawer : null,
        ws_url: process.env.WS_CONFIGURATOR,
        ws_auth: process.env.WS_CONFIGURATOR_AUTH_KEY,
        currency_exchange: this.settings?.currency_exchange?.enabled
          ? this.settings?.currency_exchange?.value ?? 1
          : 1,
        priceFormatter: this.$priceFormat,
        sticky_slider_mobile: {
          sticky_offset_top: this.sticky_offset_top,
          sticky_offset_bottom: this.sticky_offset_bottom,
          sticky_container_el: ".product-sticky-container",
          sticky_side: "",
        },
        sticky_add_to_bag_mobile: {
          sticky_container_el: ".product-sticky-container",
          sticky_side: "bottom",
          portal_el: "#add-to-bag-sticky",
        },
        container_class: "container",
        product_details:
          this.productData?.mixedTranslation?.content?.details_map,
        compare_enabled: false,
        enabled_compliment_block: true,
      });
      await this.$nextTick();
      this.isIndivInited = true;
    }

    if (this.isEmbedded) {
      this.setRecentlyViewed();
    }
  },
  beforeDestroy() {
    if (window?.confm8ht77?.destroy) {
      window.confm8ht77.destroy();
    }
    this.resizeObserver.unobserve(this.$refs["product-accordions"]);
  },
  methods: {
    ...mapActions({
      setWishlist: "wishlist/setWishlist",
      setCartInfo: "cart/setCartInfo",
    }),
    ...mapMutations({
      setCompareList: "comparison/SET_COMPARE_LIST",
      toggleCartDrawer: "general/TOGGLE_CART_DRAWER",
    }),
    // linkBuilder,
    setBreadcrumbs(productTitle) {
      this.$store.commit("general/SET_BREADCRUMBS", [
        {
          text: this.$t("home"),
          disabled: false,
          href: this.localePath("/"),
        },
        {
          text: productTitle || "",
          disabled: true,
          href: this.localePath(`product/${this.$route.params.slug}`),
        },
      ]);
    },
    setRecentlyViewed(data) {
      const maxItems = 5;
      const payload = {
        image: data?.image?.url || getProductImages(this.productData)?.[0]?.url,
        title: data?.title || this.productTitleBasedOnEmbed,
        id: this.productData?.id,
        slug: this.productData?.slug,
        price: data?.price || this.productPriceBasedOnEmbed,
        configuration_id: data?.configuration_id,
        embedded: this.productData.embedded,
        translations: this.productData?.translations,
        lang: this.$i18n.locale,
      };
      const items = this.getRecentlyViewed();

      const itemExist = items.reduce((acc, el) => {
        // Existed item in recently view array
        acc =
          el.lang === this.$i18n.locale &&
          el.slug === this.productData?.slug &&
          el?.configuration_id === data?.configuration_id
            ? acc + 1
            : acc;
        return acc;
      }, 0);
      if (!itemExist) {
        if (
          items.filter((el) => el.lang === this.$i18n.locale).length >= maxItems
        ) {
          let index = items // index of the last item with the same language
            .slice()
            .reverse()
            .findIndex((item) => item.lang === this.$i18n.locale);
          if (index !== -1) {
            index = items.length - 1 - index; // get the actual index as we searched in the array
            items.splice(index, 1);
          }
        }
        items.unshift(payload);
        localStorage.setItem("recentlyViewed", JSON.stringify(items));
      }
    },
    getRecentlyViewed() {
      const localStorageItems = JSON.parse(
        localStorage.getItem("recentlyViewed")
      );
      return localStorageItems || [];
    },
    onConfigurationChange(data) {
      this.currentConfigurationData = data;
      if (this.configurationLoaded) {
        this.setGaViewItemEvent(data);
        this.fbViewContentEvent(this.productData, data);

        this.$store.commit("general/SET_JSON_SCHEMA_DATA", {
          pageName: this.productData.mixedTranslation?.title,
          pageImage: getProductImages(this.productData)?.[0]?.url || "",
          product: this.productData,

          productSku:
            this.currentConfigurationData.configuration_id ||
            this.$route?.query?.config ||
            "",
          productPrice: this.configurationPrice,
        });
      }
      this.configurationLoaded = true;
      this.updateRouteParams();
    },
    updateRouteParams() {
      // Needed for embedded products, so that our system route is updated when the embedded configurator changes the parameters in url
      const searchParams = new URLSearchParams(window.location.search);
      const query = {};
      for (const [key, value] of searchParams.entries()) {
        query[key] = value;
      }
      this.$router.replace({ query }).catch(() => {});
    },
    setGaViewItemEvent(data) {
      const category = this.productData?.categories?.[0];
      this.gaViewItemEvent(this.productData, data);
      this.gaDynamicParamsEvent("View Product", {
        ecomm_prodid: data.configuration_id,
        ecomm_pagetype: "product",
        ecomm_totalvalue: this.googleAnalyticsPriceFormat(data.base_cost),
        ecomm_category: category?.mixedTranslation?.title || "",
      });
    },
    async pdpAddToBag(data) {
      const files = [];

      data.images.forEach((el) => {
        files.push(DataURIToBlob(el));
      });

      const options = {};

      const formData = new FormData();

      if (this.productData?.embedded) {
        formData.append("cost", data.cost);
      }

      files.forEach((image) => {
        formData.append("images[]", image);
      });
      formData.append("cart_unique_id", this.cartId);
      formData.append("image", files[0]);
      formData.append("sku", data.configuration_id);
      formData.append("vendor_model", this.productData?.vendor_model);
      formData.append("quantity", "1");
      formData.append("options", JSON.stringify(options));

      if (data.engraving) {
        formData.append("engraving_options", JSON.stringify(data.engraving));
      }

      try {
        await this.setCartItem(formData);
        this.toggleCartDrawer();
        this.gaAddToCartEvent(this.productData, data);
        this.fbAddToCartEvent(this.productData, data);
        this.addToCartEvent({
          product_id: this.productData.id,
          configuration_id: this.currentConfigurationData.configuration_id,
          collection_id: this.productData.collections?.[0]?.id,
          product_sku: this.productData.vendor_model,
          product_price: data.cost,
        });
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
      }
    },
    async setCartItem(payload) {
      try {
        const { data } = await this.$api.cart.setCartItem(payload);
        this.setCartInfo(data.data);
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
      }
    },
    async onWishlist(payload) {
      try {
        const wishlistUniqueId = this.$store.getters.getWishListId;
        let wishlist = JSON.parse(
          JSON.stringify(this.$store.state.wishlist.wishlist)
        );
        if (!wishlist) {
          wishlist = [];
        }

        const wishlistEventData = {
          product_price:
            payload?.value?.cost || this.productPriceBasedOnEmbed || 0,
          product_title: this.productTitleBasedOnEmbed,
          product_id: this.productData?.id,
          configuration_id: payload.value.sku,
        };

        if (payload.status === "add") {
          wishlist.push({
            ...payload.value,
            slug: this.productData?.slug,
            title: this.productTitleBasedOnEmbed,
            price: payload?.value?.cost || 0,
            embedded: this.productData?.embedded,
            translations: this.productData?.translations,
            vendor_model: this.productData?.vendor_model,
            id: this.productData?.id,
            collections_ids:
              this.productData?.collections?.map((item) => item?.id) || [],
            configuration_id: payload.value.sku,
            product_id: this.productData?.id,
          });

          this.gaAddToWishlistEvent(wishlistEventData);
          this.fbAddToWishlistEvent(wishlistEventData);
        } else {
          wishlist = wishlist.filter(
            (el) =>
              !isEqual(
                { sku: el.sku, engraving: el.engraving },
                { engraving: payload.value.engraving, sku: payload.value.sku }
              )
          );

          this.gaRemoveFromWishlistEvent(wishlistEventData);
          this.fbRemoveFromWishlistEvent(wishlistEventData);
        }
        const { data } = await this.$api.wishlist.addItem({
          unique_id: wishlistUniqueId,
          baggage: wishlist,
        });

        this.setWishlist(data.data.baggage);

        const eventPayload = {
          product_id: this.productData?.id,
          configuration_id: payload?.value?.sku || "",
          product_sku: this.productData?.vendor_model,
          product_price: payload?.value?.cost || "",
          collection_id: this.productData?.collections?.[0]?.id || "",
        };

        if (payload.status === "add") {
          this.addToFavoritesEvent(eventPayload);
        } else {
          this.removeFromFavoritesEvent(eventPayload);
        }
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
      }
    },
    async onCompare(payload) {
      try {
        const comparisonUniqueId =
          this.$store.state.localStorage.comparlist_unique_id;
        let compareList = JSON.parse(
          JSON.stringify(this.$store.state.comparison.compareList)
        );
        if (!compareList) {
          compareList = [];
        }
        if (payload.status === "add") {
          compareList.push({
            ...payload.value,
            slug: this.productData?.slug,
            title: this.productTitleBasedOnEmbed,
            price: payload?.value?.cost || this.productPriceBasedOnEmbed || 0,
            translations: this.productData?.translations,
          });
        } else {
          compareList = compareList.filter(
            (el) =>
              !isEqual(
                { sku: el.sku, engraving: el.engraving },
                { engraving: payload.value.engraving, sku: payload.value.sku }
              )
          );
        }
        const { data } = await this.$api.comparison.addItem({
          unique_id: comparisonUniqueId,
          baggage: compareList,
        });

        this.setCompareList(data.data.baggage);
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
      }
    },
    stickyResizeDiv() {
      this.customEvent = new Event("pdpResizeEvent");
      this.resizeObserver = new ResizeObserver((entries) => {
        if (this.customEvent) {
          window.dispatchEvent(this.customEvent);
        }
      });
      this.resizeObserver.observe(this.$refs["product-accordions"]);
    },
    parseAdditionalProperties(string) {
      // eslint-disable-next-line array-callback-return
      return string.split(";").reduce((acc, item) => {
        if (item) {
          const [property, value] = item.split("|").map((str) => str.trim());
          if (property && value) {
            acc.push({ property, value });
          }
        }
        return acc;
      }, []);
    },
    openSizeGuideDrawer() {
      this.isSizeGuideDrawerOpen = true;
    },
    onSizeGuideClick() {
      this.openSizeGuideDrawer();
    },
  },
};
